import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import {
  computeCityPrices,
  currencyFor,
  formatMoney,
  getCities,
  type Country,
} from "@/lib/gold-data";
import { useGoldRates } from "@/lib/nonblocking-rates";

const GoldChart = lazy(() =>
  import("@/components/GoldChart").then((m) => ({ default: m.GoldChart })),
);

type UnitKey =
  | "gram"
  | "8gram"
  | "10gram"
  | "tola"
  | "masha"
  | "ratti"
  | "ounce"
  | "kilogram"
  | "custom";

const UNITS: { key: UnitKey; label: string; grams: number }[] = [
  { key: "gram", label: "1 Gram", grams: 1 },
  { key: "8gram", label: "8 Gram", grams: 8 },
  { key: "10gram", label: "10 Gram", grams: 10 },
  { key: "tola", label: "Tola (11.664g)", grams: 11.6638 },
  { key: "masha", label: "Masha (0.972g)", grams: 0.972 },
  { key: "ratti", label: "Ratti (0.1215g)", grams: 0.1215 },
  { key: "ounce", label: "Ounce (31.1035g)", grams: 31.1035 },
  { key: "kilogram", label: "Kilogram (1000g)", grams: 1000 },
  { key: "custom", label: "Custom weight (enter grams)", grams: 1 },
];

const KARATS = [
  { k: 24, label: "24K (99.9% pure)" },
  { k: 23, label: "23K" },
  { k: 22, label: "22K / 22Kt (Jewellery)" },
  { k: 21, label: "21K" },
  { k: 20, label: "20K" },
  { k: 18, label: "18K" },
  { k: 17, label: "17K" },
  { k: 14, label: "14K" },
  { k: 10, label: "10K" },
  { k: 9, label: "9K" },
] as const;

function todayLabel(country: Country): string {
  const locale = country === "pakistan" ? "en-PK" : "en-IN";
  const tz = country === "pakistan" ? "Asia/Karachi" : "Asia/Kolkata";
  return new Date().toLocaleDateString(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: tz,
  });
}

function parseHash(hash: string) {
  // supports #22k, #tola, #karachi, or combined #22k-per-tola-lahore
  const parts = hash.replace(/^#/, "").toLowerCase().split(/[-_]+/);
  let karat: number | undefined;
  let unit: UnitKey | undefined;
  let city: string | undefined;
  for (let i = 0; i < parts.length; i++) {
    const p = parts[i];
    const km = p.match(/^(\d{1,2})(k|kt|carat|karat)?$/);
    if (km) {
      const n = Number(km[1]);
      if (KARATS.find((x) => x.k === n)) karat = n;
      continue;
    }
    if (p === "tola") unit = "tola";
    else if (p === "gram" || p === "grams") unit = "gram";
    else if (p === "10gram" || (p === "10" && parts[i + 1]?.startsWith("gram"))) unit = "10gram";
    else if (p === "8gram" || (p === "8" && parts[i + 1]?.startsWith("gram"))) unit = "8gram";
    else if (p === "ounce" || p === "oz") unit = "ounce";
    else if (p === "kg" || p === "kilogram" || p === "kilo") unit = "kilogram";
    else if (p === "masha") unit = "masha";
    else if (p === "ratti") unit = "ratti";
  }
  // last remaining string that isn't karat/unit → possibly city slug
  const rest = parts.filter((p) => !/^\d/.test(p) && !["tola","gram","grams","10gram","8gram","ounce","oz","kg","kilogram","kilo","masha","ratti","per","in","the"].includes(p));
  if (rest.length) city = rest.join("-");
  return { karat, unit, city };
}

export function GoldRateCalculator({ country }: { country: Country }) {
  const rates = useGoldRates().data;
  const currency = currencyFor(country);
  const cities = getCities(country);
  const defaultUnit: UnitKey = country === "pakistan" ? "tola" : "10gram";

  const [karat, setKarat] = useState<number>(country === "pakistan" ? 22 : 22);
  const [unit, setUnit] = useState<UnitKey>(defaultUnit);
  const [qty, setQty] = useState<number>(1);
  const [customGrams, setCustomGrams] = useState<number>(1);
  const [citySlug, setCitySlug] = useState<string>(cities[0].slug);
  // Jewellery bill inputs — the #1 feature gap vs ranking India/Pakistan calculators.
  const [makingMode, setMakingMode] = useState<"percent" | "perGram">("percent");
  const [making, setMaking] = useState<number>(0);
  const [taxPct, setTaxPct] = useState<number>(country === "india" ? 3 : 0);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const apply = () => {
      const sp = new URLSearchParams(window.location.search);
      const fromHash = parseHash(window.location.hash);
      const k = Number(sp.get("karat")) || fromHash.karat;
      const u = (sp.get("unit") as UnitKey | null) ?? fromHash.unit;
      const city = sp.get("city") ?? fromHash.city;
      if (k) setKarat(k);
      if (u && UNITS.some((x) => x.key === u)) setUnit(u);
      if (city && cities.find((c) => c.slug === city)) setCitySlug(city);
    };
    apply();
    window.addEventListener("hashchange", apply);
    window.addEventListener("popstate", apply);
    return () => {
      window.removeEventListener("hashchange", apply);
      window.removeEventListener("popstate", apply);
    };
  }, [cities]);

  /** Apply a deep-link combination without a page reload, then scroll to the calculator. */
  function applyCombo(next: { karat?: number; unit?: UnitKey; city?: string }) {
    const k = next.karat ?? karat;
    const u = next.unit ?? unit;
    const c = next.city ?? citySlug;
    setKarat(k);
    setUnit(u);
    setCitySlug(c);
    if (typeof window !== "undefined") {
      const url = `${window.location.pathname}?karat=${k}&unit=${u}&city=${c}`;
      window.history.replaceState(null, "", url);
      document
        .getElementById("calculator")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  const city = cities.find((c) => c.slug === citySlug) ?? cities[0];
  const fx = country === "pakistan" ? rates.pkrPerUsd : rates.inrPerUsd;

  const cityPrices = useMemo(
    () =>
      computeCityPrices({
        spotUsdPerOz: rates.spotUsdPerOz,
        fxRate: fx,
        country,
        cityPremium: city.premium,
      }),
    [rates.spotUsdPerOz, fx, country, city.premium],
  );

  const perGram24 = cityPrices.perGram24;
  const perGramK = (k: number) => (perGram24 * k) / 24;
  const unitObj = UNITS.find((u) => u.key === unit)!;
  const unitGrams = unit === "custom" ? Math.max(0, customGrams) : unitObj.grams;
  const totalGrams = unitGrams * qty;
  const totalPrice = perGramK(karat) * totalGrams;
  const makingAmount = makingMode === "percent" ? totalPrice * (making / 100) : making * totalGrams;
  const taxAmount = (totalPrice + makingAmount) * (taxPct / 100);
  const finalBill = totalPrice + makingAmount + taxAmount;
  const hasBill = makingAmount > 0 || taxAmount > 0;

  return (
    <div className="space-y-10">
      {/* Chart on top */}
      <section className="mx-auto max-w-6xl px-4 pt-6">
        <Suspense fallback={<div className="h-72 rounded-xl border border-border bg-card" />}>
          <GoldChart country={country} />
        </Suspense>
      </section>

      {/* Today date */}
      <section className="mx-auto max-w-4xl px-4">
        <div className="rounded-xl border border-border bg-primary/5 px-5 py-3 text-center">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Today's Live Gold Rate</p>
          <p className="mt-1 text-lg font-semibold">{todayLabel(country)}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Base 24K in {city.name}: <span className="font-mono font-bold">{formatMoney(country === "pakistan" ? cityPrices.perTola24 : cityPrices.per10g24, currency)}</span> {country === "pakistan" ? "/ tola" : "/ 10g"}
          </p>
        </div>
      </section>

      {/* Calculator */}
      <section id="calculator" className="mx-auto max-w-4xl px-4 scroll-mt-20">
        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
          <h2 className="text-2xl font-bold">
            {country === "pakistan"
              ? "Today Gold Rate Calculator Pakistan"
              : "Today Gold Rate Calculator India"}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {country === "pakistan"
              ? "Live rate per tola, per gram, per 10 gram, masha aur ratti — 24K, 22K, 21K, 18K, 17K, 14K. City badlein aur instant calculation dekhein."
              : "Live rate per 10 gram, per gram, per tola aur per 8 gram — 24 carat, 22kt, 21K, 18K, 17K, 14K sabhi karat. Apna sheher chunein aur instant total calculate karein."}
          </p>

          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Karat / Purity</span>
              <select
                value={karat}
                onChange={(e) => setKarat(Number(e.target.value))}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm font-semibold"
              >
                {KARATS.map((k) => (
                  <option key={k.k} value={k.k}>{k.label}</option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Unit / Weight</span>
              <select
                value={unit}
                onChange={(e) => setUnit(e.target.value as UnitKey)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm font-semibold"
              >
                {UNITS.map((u) => (
                  <option key={u.key} value={u.key}>{u.label}</option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Quantity</span>
              <input
                type="number"
                min={0}
                step="0.01"
                value={qty}
                onChange={(e) => setQty(Math.max(0, parseFloat(e.target.value) || 0))}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm font-semibold tabular-nums"
              />
            </label>

            {unit === "custom" && (
              <label className="block">
                <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Custom weight (grams)
                </span>
                <input
                  type="number"
                  min={0}
                  step="0.001"
                  value={customGrams}
                  onChange={(e) => setCustomGrams(Math.max(0, parseFloat(e.target.value) || 0))}
                  placeholder="e.g. 1.2"
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm font-semibold tabular-nums"
                />
                <span className="mt-1 block text-[11px] text-muted-foreground">
                  Tip: 1.3 tola = {(1.3 * 11.6638).toFixed(3)} g · 1.2 gram = 1.2 g
                </span>
              </label>
            )}

            <label className="block">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">City / Sarafa Bazaar</span>
              <select
                value={citySlug}
                onChange={(e) => setCitySlug(e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm font-semibold"
              >
                {cities.map((c) => (
                  <option key={c.slug} value={c.slug}>{c.name}</option>
                ))}
              </select>
            </label>
          </div>

          <fieldset className="mt-6 rounded-xl border border-border p-4">
            <legend className="px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Jewellery bill (optional)
            </legend>
            <div className="grid gap-4 sm:grid-cols-3">
              <label className="block">
                <span className="mb-1 block text-xs font-semibold text-muted-foreground">Making charge type</span>
                <select
                  value={makingMode}
                  onChange={(e) => setMakingMode(e.target.value as "percent" | "perGram")}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm font-semibold"
                >
                  <option value="percent">% of gold value</option>
                  <option value="perGram">{currency} per gram</option>
                </select>
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-semibold text-muted-foreground">
                  Making charge {makingMode === "percent" ? "(%)" : `(${currency}/g)`}
                </span>
                <input
                  type="number"
                  min={0}
                  step="0.1"
                  value={making}
                  onChange={(e) => setMaking(Math.max(0, parseFloat(e.target.value) || 0))}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm font-semibold tabular-nums"
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-semibold text-muted-foreground">
                  {country === "india" ? "GST (%)" : "Sales tax (%)"}
                </span>
                <input
                  type="number"
                  min={0}
                  max={30}
                  step="0.1"
                  value={taxPct}
                  onChange={(e) => setTaxPct(Math.min(30, Math.max(0, parseFloat(e.target.value) || 0)))}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm font-semibold tabular-nums"
                />
              </label>
            </div>
            <p className="mt-2 text-[11px] text-muted-foreground">
              {country === "india"
                ? "India: 3% GST applies to gold value plus making charges. Making is usually 8–25% for jewellery and 0–3% for coins."
                : "Pakistan: sarafa shops usually quote making (banwai) per tola or as a percentage. Leave tax at 0 unless your invoice shows one."}
            </p>
          </fieldset>

          <div className="mt-6 min-h-[190px] rounded-xl border border-primary/30 bg-primary/5 p-5 text-center">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">
              {qty} × {unit === "custom" ? `${unitGrams} g (custom)` : unitObj.label} × {karat}K in {city.name}
            </p>
            <p className="mt-2 text-4xl font-black tabular-nums text-primary">
              {formatMoney(hasBill ? finalBill : totalPrice, currency)}
            </p>
            <p className="mt-2 text-xs text-muted-foreground">
              Rate per gram ({karat}K, {city.name}): <span className="font-mono font-bold">{formatMoney(perGramK(karat), currency)}</span>
            </p>
            {hasBill && (
              <dl className="mx-auto mt-4 max-w-sm space-y-1 text-left text-sm">
                <div className="flex justify-between"><dt className="text-muted-foreground">Gold value ({totalGrams.toFixed(3)} g)</dt><dd className="tabular-nums">{formatMoney(totalPrice, currency)}</dd></div>
                <div className="flex justify-between"><dt className="text-muted-foreground">Making charges</dt><dd className="tabular-nums">{formatMoney(makingAmount, currency)}</dd></div>
                <div className="flex justify-between"><dt className="text-muted-foreground">{country === "india" ? "GST" : "Tax"} ({taxPct}%)</dt><dd className="tabular-nums">{formatMoney(taxAmount, currency)}</dd></div>
                <div className="flex justify-between border-t border-border pt-1 font-bold"><dt>Final bill</dt><dd className="tabular-nums">{formatMoney(finalBill, currency)}</dd></div>
              </dl>
            )}
          </div>

          {/* Full karat matrix */}
          <div className="mt-6 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase">
                <tr>
                  <th className="px-3 py-2 text-left">Karat</th>
                  <th className="px-3 py-2 text-right">Per Gram</th>
                  <th className="px-3 py-2 text-right">{country === "pakistan" ? "Per Tola" : "Per 10 Gram"}</th>
                  <th className="px-3 py-2 text-right">Per Ounce</th>
                </tr>
              </thead>
              <tbody>
                {KARATS.map((k) => (
                  <tr key={k.k} id={`${k.k}k`} className="border-t border-border">
                    <td className="px-3 py-2 font-semibold">{k.k}K</td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatMoney(perGramK(k.k), currency)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatMoney(perGramK(k.k) * (country === "pakistan" ? 11.6638 : 10), currency)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatMoney(perGramK(k.k) * 31.1035, currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Deep-link mesh — hash anchors for city × unit × karat variants */}
      <section id="cities" className="mx-auto max-w-6xl px-4">
        <h2 className="text-xl font-bold">Calculate by City & Karat</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Click any combination — the calculator above updates instantly and the page scrolls back up to show your result. Each combination has its own shareable URL.
        </p>
        <div className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {cities.slice(0, 12).map((c) => (
            <div key={c.slug} id={c.slug} className="rounded-xl border border-border bg-card p-4">
              <h3 className="font-bold">{c.name}</h3>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {[24, 22, 21, 18, 14].map((k) => (
                  <a
                    key={k}
                    href={`?karat=${k}&unit=${defaultUnit}&city=${c.slug}`}
                    onClick={(e) => {
                      e.preventDefault();
                      applyCombo({ karat: k, unit: defaultUnit, city: c.slug });
                    }}
                    className="rounded-md border border-border bg-background px-2 py-1 text-xs hover:border-primary hover:bg-primary/10"
                  >
                    {k}K
                  </a>
                ))}
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {UNITS.filter((u) => u.key !== "custom").map((u) => (
                  <a
                    key={u.key}
                    href={`?karat=${karat}&unit=${u.key}&city=${c.slug}`}
                    onClick={(e) => {
                      e.preventDefault();
                      applyCombo({ karat, unit: u.key, city: c.slug });
                    }}
                    className="rounded-md border border-border bg-background px-2 py-1 text-[11px] hover:border-primary hover:bg-primary/10"
                  >
                    {u.label.split(" ")[0]}
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}